/**
 * Check-in iniciado pelo QR fixo, com intenção e proximidade validadas no servidor.
 *
 * O cliente NUNCA decide se o check-in é válido: apenas coleta a posição do GPS
 * após a escolha da intenção e envia o fator presencial para a Edge Function
 * `validate-checkin`. A função deriva a identidade do JWT e chama uma RPC privada.
 */
import { supabase } from "@/integrations/supabase/client";
import { loadNativeGeolocation } from "@/lib/native-geolocation";
import { z } from "zod";
import { CHECKIN_INTENTION_VALUES, type CheckinIntention } from "./checkinIntentions";

export type { CheckinIntention };

export const parseCheckinIntention = (value?: string | null): CheckinIntention | null =>
  (CHECKIN_INTENTION_VALUES as readonly string[]).includes(value ?? "")
    ? (value as CheckinIntention)
    : null;

export type CheckinFactor = {
  intention: CheckinIntention;
  groupLabel?: string;
  musicLabel?: string;
};

export type DevicePosition = {
  latitude: number;
  longitude: number;
  accuracy: number;
};

const MAX_ACCEPTED_ACCURACY_METERS = 100;

export const intentionDetailsSchema = z.object({
  intention: z.enum(CHECKIN_INTENTION_VALUES),
  groupLabel: z.string().trim().max(60, "A turma deve ter no máximo 60 caracteres."),
  musicLabel: z.string().trim().max(60, "A música deve ter no máximo 60 caracteres."),
}).superRefine((value, context) => {
  if (value.intention === "group" && !value.groupLabel) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["groupLabel"], message: "Informe sua turma." });
  }
  if (value.intention === "music" && !value.musicLabel) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["musicLabel"], message: "Informe a música." });
  }
});

export type ValidateCheckinResult = {
  ok: boolean;
  error?: string;
  message: string;
  checkin_id?: string;
  session_id?: string;
  expira_em?: string;
  declared_intention?: CheckinIntention;
};

export type PresenceRenewalResult = {
  ok: boolean;
  status?: "renewed" | "checked_out";
  reason?: "outside_radius";
  error?: string;
  expira_em?: string;
};

export type ReturningCheckinPreference = {
  intention: CheckinIntention;
  groupLabel: string;
  musicLabel: string;
};

const ERROR_MESSAGES: Record<string, string> = {
  unauthenticated: "Faça login para confirmar o check-in.",
  venue_not_found: "Estabelecimento não encontrado ou inativo.",
  too_far: "Você está fora do raio deste local.",
  rate_limited: "Muitas tentativas. Aguarde alguns minutos e tente de novo.",
  already_checked_in: "Você já está com check-in ativo neste local.",
  age_verification_required: "Confirme a idade",
  invalid_intention: "Escolha o que você veio fazer antes de confirmar.",
  invalid_group: "Informe uma turma válida, com até 60 caracteres.",
  invalid_music: "Informe uma música válida, com até 60 caracteres.",
  profile_name_required: "Adicione seu nome ao perfil antes de fazer check-in.",
  validation_failed: "Não foi possível validar o check-in agora.",
  gps_denied:
    "Precisamos da sua localização para confirmar que você está no local. Libere o GPS nas configurações do navegador/app.",
  gps_unavailable: "Não conseguimos ler seu GPS. Tente novamente em alguns segundos.",
  gps_low_accuracy: "O sinal do GPS está impreciso. Vá para perto de uma janela ou área aberta e localize novamente.",
  no_venue_in_range: "Não encontramos um estabelecimento disponível neste ponto.",
  presence_expired: "Sua presença expirou. Identifique o local novamente para confirmar.",
  internal_error: "Erro inesperado. Tente novamente.",
};

export const checkinErrorMessage = (code?: string | null): string =>
  (code && ERROR_MESSAGES[code]) || ERROR_MESSAGES.validation_failed;

const readFunctionErrorCode = async (error: unknown): Promise<string> => {
  const context = (error as { context?: unknown })?.context;
  if (!context || typeof context !== "object") return "validation_failed";

  try {
    if (context instanceof Response) {
      const body = await context.clone().json() as { error?: unknown };
      return typeof body.error === "string" ? body.error : "validation_failed";
    }

    const body = context as { error?: unknown };
    return typeof body.error === "string" ? body.error : "validation_failed";
  } catch {
    return "validation_failed";
  }
};

export function parseReturningCheckinPreference(value: unknown): ReturningCheckinPreference | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as { eligible?: unknown; declared_intention?: unknown; group_label?: unknown; music_label?: unknown };
  if (candidate.eligible !== true) return null;

  const parsed = intentionDetailsSchema.safeParse({
    intention: candidate.declared_intention,
    groupLabel: typeof candidate.group_label === "string" ? candidate.group_label : "",
    musicLabel: typeof candidate.music_label === "string" ? candidate.music_label : "",
  });
  if (!parsed.success) return null;

  return {
    intention: parsed.data.intention,
    groupLabel: parsed.data.groupLabel,
    musicLabel: parsed.data.musicLabel,
  };
}

export async function getReturningCheckinPreference(venueId: string): Promise<ReturningCheckinPreference | null> {
  const { data, error } = await supabase.functions.invoke("validate-checkin", {
    body: { action: "returning_preference", venue_id: venueId },
  });
  if (error) return null;
  return parseReturningCheckinPreference(data);
}

const getWebPosition = (): Promise<GeolocationPosition> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("gps_unavailable"));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0,
    });
  });

const positionIsUsable = (position: DevicePosition): boolean =>
  Number.isFinite(position.latitude) &&
  position.latitude >= -90 &&
  position.latitude <= 90 &&
  Number.isFinite(position.longitude) &&
  position.longitude >= -180 &&
  position.longitude <= 180 &&
  Number.isFinite(position.accuracy) &&
  position.accuracy > 0;

export async function acquireDevicePosition(): Promise<
  { ok: true; position: DevicePosition } | { ok: false; error: string; message: string }
> {
  try {
    const nativeGeolocation = await loadNativeGeolocation();
    let position: DevicePosition;
    if (nativeGeolocation) {
      const permission = await nativeGeolocation.checkPermissions();
      if (permission.location !== "granted") {
        const requested = await nativeGeolocation.requestPermissions();
        if (requested.location !== "granted") {
          return { ok: false, error: "gps_denied", message: checkinErrorMessage("gps_denied") };
        }
      }
      const nativePosition = await nativeGeolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      });
      position = nativePosition.coords;
    } else {
      const webPosition = await getWebPosition();
      position = {
        latitude: webPosition.coords.latitude,
        longitude: webPosition.coords.longitude,
        accuracy: webPosition.coords.accuracy,
      };
    }
    if (!positionIsUsable(position)) {
      return { ok: false, error: "gps_unavailable", message: checkinErrorMessage("gps_unavailable") };
    }
    if (position.accuracy > MAX_ACCEPTED_ACCURACY_METERS) {
      return { ok: false, error: "gps_low_accuracy", message: checkinErrorMessage("gps_low_accuracy") };
    }
    return { ok: true, position };
  } catch (error) {
    const code = typeof error === "object" && error !== null ? Number((error as { code?: unknown }).code) : 0;
    const errorText = typeof error === "object" && error !== null && "message" in error
      ? String((error as { message?: unknown }).message)
      : String(error);
    const denied = code === 1 || errorText.toLowerCase().includes("denied");
    const errorCode = denied ? "gps_denied" : "gps_unavailable";
    return { ok: false, error: errorCode, message: checkinErrorMessage(errorCode) };
  }
}

export async function validateVenueCheckin(
  venueId: string,
  factor: CheckinFactor,
  confirmedPosition?: DevicePosition,
): Promise<ValidateCheckinResult> {
  const details = intentionDetailsSchema.safeParse({
    intention: factor.intention,
    groupLabel: factor.groupLabel ?? "",
    musicLabel: factor.musicLabel ?? "",
  });
  if (!details.success) {
    const field = details.error.issues[0]?.path[0];
    const code = field === "groupLabel" ? "invalid_group" : field === "musicLabel" ? "invalid_music" : "invalid_intention";
    return { ok: false, error: code, message: checkinErrorMessage(code) };
  }
  let position = confirmedPosition;
  if (!position) {
    const located = await acquireDevicePosition();
    if (!("position" in located)) {
      return { ok: false, error: located.error, message: located.message };
    }
    position = located.position;
  }
  if (!positionIsUsable(position) || position.accuracy > MAX_ACCEPTED_ACCURACY_METERS) {
    const code = "gps_low_accuracy";
    return { ok: false, error: code, message: checkinErrorMessage(code) };
  }

  const payload = {
    action: "confirm_location",
    venue_id: venueId,
    declared_intention: factor.intention,
    group_label: details.data.groupLabel,
    music_label: details.data.musicLabel,
    lat: position.latitude,
    lng: position.longitude,
    accuracy_m: position.accuracy,
  };

  try {
  const { data, error } = await supabase.functions.invoke("validate-checkin", {
    body: payload,
  });

  if (error) {
    const code = await readFunctionErrorCode(error);
    return { ok: false, error: code, message: checkinErrorMessage(code) };
  }

  const result = (data ?? {}) as ValidateCheckinResult;
  if (!result.ok) {
    return { ...result, ok: false, message: checkinErrorMessage(result.error) };
  }

  return { ...result, ok: true, message: "Check-in confirmado!" };
  } catch {
    return { ok: false, error: "validation_failed", message: "Não foi possível confirmar sua presença. Verifique sua conexão e tente novamente." };
  }
}

export async function renewVenuePresence(
  venueId: string,
  position: DevicePosition,
): Promise<PresenceRenewalResult> {
  if (!positionIsUsable(position) || position.accuracy > MAX_ACCEPTED_ACCURACY_METERS) {
    return { ok: false, error: "gps_low_accuracy" };
  }

  const { data, error } = await supabase.functions.invoke("validate-checkin", {
    body: {
      action: "renew_location",
      venue_id: venueId,
      lat: position.latitude,
      lng: position.longitude,
      accuracy_m: position.accuracy,
    },
  });

  if (error) return { ok: false, error: await readFunctionErrorCode(error) };
  return (data ?? { ok: false, error: "validation_failed" }) as PresenceRenewalResult;
}
